import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircleIcon, AlertTriangleIcon, InfoIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";

const alertVariants = cva(
  "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
  {
    variants: {
      variant: {
        info: "border-border bg-muted text-foreground",
        warning: "border-warning/50 bg-warning/15 text-foreground",
        destructive: "border-destructive/40 bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

const icons = {
  info: InfoIcon,
  warning: AlertTriangleIcon,
  destructive: AlertCircleIcon,
};

export interface AlertProps extends ComponentProps<"div">, VariantProps<typeof alertVariants> {
  /** Set to false where the sentence already carries its own mark. */
  icon?: boolean;
  children?: ReactNode;
}

/**
 * A band that says something the screen cannot say with a number: a customer who is over their
 * limit, a shelf that will not cover an order (US-37.4).
 *
 * <p>It states a fact and does not stop anybody. Everything it warns about is still refused by the
 * server if it has to be — and it may not have to: a credit exception can be granted between this
 * render and the button. Which is why it is a band above the form, and not a disabled button.
 */
export function Alert({ className, variant, icon = true, children, ...props }: AlertProps) {
  const Icon = icons[variant ?? "info"];
  return (
    <div role="status" className={cn(alertVariants({ variant }), className)} {...props}>
      {icon ? <Icon className="mt-0.5 size-4 shrink-0" aria-hidden /> : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export { alertVariants };
